import { useEffect, useState, useMemo } from "react";
import Search from "../../icons/Search";
import Add from "../../icons/Add";
import Modal from "react-modal";
import { Form, Input, Select, Button, message } from 'antd';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { api, API_BASE_URL } from "../../config/api";
import { useMyContext } from "../../context/MyContext";

const { Option } = Select;

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "500px",
    maxWidth: "90vw",
    height: "auto",
    minHeight: "400px",
    maxHeight: "90vh",
    padding: "20px",
    borderRadius: "10px",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.79)",
    zIndex: 100,
  },
};

// Email validation regex
const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Password validation rules
const passwordRules = {
  minLength: 8,
  maxLength: 20,
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasNumber: /\d/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
};

const AdminHeader = ({ filteredData, setFilteredData, Data }) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const { user } = useMyContext();
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const addMutation = useMutation({
    mutationFn: async (values) => {
      const userData = { ...values, role: values.role };
      const res = await api.post("/users/admin", userData);
      if (res.status === 201) {
        let { uid } = res.data;
        try {
          await api.post(`${API_BASE_URL}/mail/send-admin-welcome`, userData);
          message.success("Admin created and welcome email sent successfully!");
        } catch (emailError) {
          console.error("Error sending welcome email:", emailError);
          toast.warning("Admin created but failed to send welcome email");
        }
        return uid;
      }
      throw new Error("Failed to create admin");
    },
    mutationKey: ["all-admins"],
    onSuccess: (newUid) => {
      queryClient.invalidateQueries({ queryKey: ["all-admins"] });
      toast.success("Admin added successfully!");
      setIsOpen(false);
      form.resetFields();
    },
    onError: (error) => {
      if (error.code === "auth/email-already-in-use") {
        toast.error("Email is already registered. Please use a different email.");
      } else {
        toast.error(
          error?.response?.data?.message
            ? error?.response?.data?.message
            : "Error during user registration. Please try again later."
        );
      }
    },
  });

  const handleSubmit = (values) => {
    addMutation.mutate(values);
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    form.resetFields();
    setIsOpen(false);
  };

  // Enhanced search to include both name and email
  const filteredAdmins = useMemo(() => {
    if (!Data || !Array.isArray(Data)) return [];
    if (searchQuery.trim() === "") return Data;

    const query = searchQuery.toLowerCase().trim();

    return Data.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const email = item.email?.toLowerCase() || "";
      return name.includes(query) || email.includes(query);
    });
  }, [Data, searchQuery]);

  // Update filteredData when filteredAdmins changes
  useEffect(() => {
    setFilteredData(filteredAdmins);
  }, [filteredAdmins, setFilteredData]);

  return (
    <>
      <div
        className="p-5 rounded-[10px] bg-[#fff] w-full"
        style={{ border: "1px solid rgba(5, 63, 146, 0.24)" }}
      >
        <div className="flex justify-between items-center space-y-2 flex-wrap">
          <div className="w-full md:w-auto flex flex-col gap-y-2 md:flex-row items-center md:space-x-2">
            <div className="w-full md:w-auto p-3 flex space-x-2 items-center rounded-[5px] border-2 border-blue">
              <Search />
              <input
                placeholder="Search by name or email"
                className="outline-none w-full"
                style={{ color: "rgba(24, 24, 35, 0.53)" }}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {user?.role === "super admin" ? (
            <div className="flex items-center gap-4">
              <div
                onClick={openModal}
                className="w-full md:w-auto flex cursor-pointer space-x-2 py-3 px-6 btnBg rounded-[5px]"
              >
                <div className="h-[24px] w-[24px] bg-[#fff] rounded-full flex justify-center items-center">
                  <Add />
                </div>
                <p className="text-[#fff]">Add Admin</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Add Admin Modal"
      >
        <h1 className="text-[#000] font-semibold text-[24px] mb-4">Add Admin</h1>

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please input the name!' },
              { min: 2, message: 'Name must be at least 2 characters!' },
              { max: 50, message: 'Name must not exceed 50 characters!' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { pattern: emailRegex, message: 'Please enter a valid email address!' },
              { max: 255, message: 'Email must not exceed 255 characters!' },
              {
                validator: (_, value) =>
                  value && !value.includes(' ')
                    ? Promise.resolve()
                    : Promise.reject('Email cannot contain spaces!'),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input the password!' },
              { min: passwordRules.minLength, message: `Password must be at least ${passwordRules.minLength} characters!` },
              { max: passwordRules.maxLength, message: `Password must not exceed ${passwordRules.maxLength} characters!` },
              { pattern: passwordRules.hasUpperCase, message: 'Password must contain at least one uppercase letter!' },
              { pattern: passwordRules.hasLowerCase, message: 'Password must contain at least one lowercase letter!' },
              { pattern: passwordRules.hasNumber, message: 'Password must contain at least one number!' },
              { pattern: passwordRules.hasSpecialChar, message: 'Password must contain at least one special character!' },
              {
                validator: (_, value) =>
                  value && !value.includes(' ')
                    ? Promise.resolve()
                    : Promise.reject('Password cannot contain spaces!'),
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select the role!' }]}
          >
            <Select>
              <Option value="super admin">Super Admin</Option>
              <Option value="managing admin">Managing Admin</Option>
              <Option value="lead admin">Lead Admin</Option>
              <Option value="editor">Editor</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={addMutation.isPending}
            >
              Add Admin
            </Button>
            <Button
              onClick={closeModal}
              style={{ marginLeft: 8 }}
              disabled={addMutation.isPending}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminHeader;